import React, { useEffect, useState, useRef } from "react";

const ChatWindow = ({ ticket, closeChat }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const fetchMessages = async () => {
    console.log(`[ChatWindow] Fetching messages for ticket #${ticket.ticket_id}`);
    console.log(`[ChatWindow] API URL: ${apiBaseURL}/api/tickets/${ticket.ticket_id}/messages`);
    
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseURL}/api/tickets/${ticket.ticket_id}/messages`);
      console.log(`[ChatWindow] Fetch response status: ${response.status}`);

      if (!response.ok) {
        console.error(`[ChatWindow] Fetch failed with status ${response.status}`);
        throw new Error("Failed to fetch messages");
      }

      const data = await response.json();
      console.log(`[ChatWindow] Fetched message data:`, data);
      console.log(`[ChatWindow] Total messages received: ${data.length}`);
      setMessages(data);
      console.log(`[ChatWindow] Messages state updated.`);
    } catch (err) {
      setError("Error fetching messages.");
      console.error(`[ChatWindow] Error fetching messages:`, err);
    } finally {
      setLoading(false);
      console.log(`[ChatWindow] fetchMessages() operation complete.`);
    }
  };

  useEffect(() => {
    console.log("[ChatWindow] Component mounted:", ticket);  // Log the whole ticket object

    // Check for ticket status explicitly and log properly
    if (ticket && ticket.status) {
      console.log(`[ChatWindow] Ticket status: ${ticket.status}`);
    } else {
      console.warn(`[ChatWindow] Ticket status is missing or undefined.`);
    }

    fetchMessages();
  }, [ticket]); // Ensure that the effect reruns whenever `ticket` changes

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      console.log(`[ChatWindow] Scrolled to latest message.`);
    }
  }, [messages]);

  const handleSendMessage = async () => {
    console.log(`[ChatWindow] Attempting to send message: "${newMessage}"`);

    // If the ticket is closed, prevent sending messages
    if (ticket.status === "closed") {
      console.warn(`[ChatWindow] Ticket is closed. Cannot send message.`);
      alert("Cannot send messages to a closed ticket.");
      return;
    }

    // If the message is empty, ignore sending
    if (!newMessage.trim()) {
      console.log(`[ChatWindow] Empty message ignored.`);
      return;
    }

    const senderRole = "admin"; // You can leave this as "admin" or make it dynamic

    const ticketId = ticket.ticket_id; // Include ticket_id

    const payload = {
      sender_role: senderRole,
      message: newMessage,
    };

    console.log(`[ChatWindow] Sending POST request to: ${apiBaseURL}/api/tickets/${ticketId}/messages/send`);
    console.log(`[ChatWindow] Request payload:`, payload);

    try {
      const response = await fetch(`${apiBaseURL}/api/tickets/${ticketId}/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload), // ticket_id is sent in the URL, not the body
      });

      console.log(`[ChatWindow] POST response status: ${response.status}`);

      if (!response.ok) {
        const responseData = await response.json();
        console.error(`[ChatWindow] POST request failed with status ${response.status}:`, responseData.message);
        throw new Error(responseData.message || "Failed to send message");
      }

      const responseData = await response.json();
      console.log(`[ChatWindow] Response data:`, responseData);

      if (responseData.message_id) {
        const newMsgObj = {
          message: newMessage,
          sender_role: senderRole,
          ticket_id: ticketId, // Include ticket_id
        };
        setMessages((prev) => {
          const updated = [...prev, newMsgObj];
          console.log(`[ChatWindow] Updated messages state:`, updated);
          return updated;
        });
        setNewMessage("");
        console.log(`[ChatWindow] Message sent successfully. Input cleared.`);
      } else {
        console.error(`[ChatWindow] API responded with failure:`, responseData.message);
        alert(responseData.message || "Failed to send message.");
      }
    } catch (err) {
      setError("Error sending message.");
      console.error(`[ChatWindow] Error sending message:`, err);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    console.log(`[ChatWindow] Message input changed: "${e.target.value}"`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl h-[90vh] rounded-lg shadow-lg flex flex-col relative">
        {/* Close chat window button */}
        <button
          onClick={() => {
            console.log(`[ChatWindow] Closing chat window.`);
            closeChat();
          }}
          className="absolute top-3 right-3 text-gray-600 hover:text-red-600 text-lg font-bold"
        >
          ✖
        </button>

        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold">Chat - Ticket #{ticket.ticket_id}</h2>
          <p className="text-sm text-gray-500">{ticket.title}</p>
          <p className="text-xs text-red-500">
            Status: {ticket.status}
          </p>
        </div>

        {/* Chat messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <p>Loading messages...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : messages.length === 0 ? (
            <p className="text-gray-500 text-sm">No messages yet.</p>
          ) : (
            messages
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
              .map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.sender_role === "freelancer" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                      msg.sender_role === "freelancer"
                        ? "bg-blue-100 text-left"
                        : "bg-gray-200 text-right"
                    }`}
                  >
                    <p>{msg.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(msg.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input box */}
        <div className="border-t p-3 flex items-center">
          {/* Check if the ticket is closed */}
          {ticket.status === "closed" ? (
            <>
              <p className="text-center text-sm text-gray-500 w-full">
                This ticket is closed. You cannot send messages here.
              </p>
            </>
          ) : (
            <>
              <input
                type="text"
                value={newMessage}
                onChange={handleInputChange}
                placeholder="Type your message..."
                className="flex-1 border rounded-lg p-2 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="ml-3 bg-blue-500 text-white py-2 px-4 rounded-lg text-sm disabled:bg-gray-400"
              >
                Send
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
